import { ValidationIssue } from '@intelligent-play-v2/domain';

/**
 * The review form's visual grammar for "this still needs attention".
 *
 * Scanning the form for colour should tell the reviewer what is left to do, so the state
 * paints the whole control rather than just its label:
 *
 *   invalid     — blocks the commit, empty or wrong (red)
 *   outstanding — worth a look, but does not block (amber)
 *   neutral     — nothing to say
 *
 * Uses the existing palette: `red` is the house error colour, `yellow` the warning one.
 */
export type FieldState = 'neutral' | 'outstanding' | 'invalid';

/** Tailwind classes painting an input for the given state. */
export const inputStateClass = (state: FieldState): string => {
  switch (state) {
    case 'outstanding':
      return 'bg-yellow-lightest border border-yellow';
    case 'invalid':
      return 'bg-red-lightest border border-red';
    default:
      return '';
  }
};

/**
 * The state to paint a control, from the live validation result.
 *
 * Severity is the signal, not emptiness: anything that blocks the commit is red, whether
 * it is empty or wrong, so a reviewer opening a fresh draft can see at a glance which
 * fields the form never filled in. Amber is left for advice that does not block — a
 * near-miss facility name, an overlapping usage range.
 */
export const fieldStateFor = (
  issues: ValidationIssue[],
  path: string
): FieldState => {
  const here = issuesAt(issues, path);
  if (here.some(issue => issue.severity === 'error')) {
    return 'invalid';
  }
  return here.length > 0 ? 'outstanding' : 'neutral';
};

/** Issues whose `path` matches exactly. */
export const issuesAt = (
  issues: ValidationIssue[],
  path: string
): ValidationIssue[] => issues.filter(issue => issue.path === path);

/** Issues at a path or anywhere beneath it, for summarising a whole section. */
export const issuesUnder = (
  issues: ValidationIssue[],
  pathPrefix: string
): ValidationIssue[] =>
  issues.filter(
    issue =>
      issue.path === pathPrefix || issue.path.startsWith(`${pathPrefix}.`)
  );

/**
 * The first *blocking* message at a path, for an input's `errorMessage` — which paints the
 * control red and shows the message in place of its label. Warnings are painted amber
 * instead, so they do not borrow the error styling.
 */
export const messageAt = (
  issues: ValidationIssue[],
  path: string
): string | undefined =>
  issuesAt(issues, path).find(issue => issue.severity === 'error')?.message;
